import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { OrganizationFeatureField } from 'components/OrganizationFeatureField'
import { OrganizationPlanField } from 'components/OrganizationPlanField'
import { routes } from 'constants/routes'
import { commonStrings, lineStrings, organizationManagementStrings } from 'constants/strings'
import { useCreateOrganization } from 'hooks'
import { EOrganizationPlan } from 'types/enum'
import type { TOrganizationFeatures } from 'types/organization'
import { DISABLED_ORGANIZATION_FEATURES } from 'utils/organizationFeature'

export default function OrganizationManagementCreate() {
  const navigate = useNavigate()
  const strings = organizationManagementStrings.create

  const [name, setName] = useState('')
  const [plan, setPlan] = useState<EOrganizationPlan>(EOrganizationPlan.INTERNAL)
  const [features, setFeatures] = useState<TOrganizationFeatures>({
    ...DISABLED_ORGANIZATION_FEATURES,
  })
  const [validationError, setValidationError] = useState<string | undefined>()

  const { create, isSubmitting, error: apiError } = useCreateOrganization({
    onSuccess: () =>
      navigate(routes.ORGANIZATION_MANAGEMENT, {
        replace: true,
        state: { organizationCreated: true },
      }),
  })

  const error = validationError || apiError

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    setValidationError(undefined)

    const trimmedName = name.trim()
    if (!trimmedName) {
      setValidationError(strings.errors.required)
      return
    }

    create({ name: trimmedName, plan, features })
  }

  return (
    <ManagementFormLayout
      kicker={strings.kicker}
      title={strings.title}
      description={strings.description}
      backTo={routes.ORGANIZATION_MANAGEMENT}
    >
      <form
        className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {strings.form.title}
          </h2>
          <p className="text-xs text-muted">{strings.form.description}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          {lineStrings.providerNotice.map((line, i) => (
            <p key={i} className="text-sm text-amber-800">{line}</p>
          ))}
        </div>
        <div className="grid gap-4">
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.name.label}
            <Input
              name="name"
              placeholder={strings.form.fields.name.placeholder}
              autoComplete="organization"
              required
              disabled={isSubmitting}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <OrganizationPlanField
            label={strings.form.fields.plan.label}
            value={plan}
            onChange={setPlan}
            disabled={isSubmitting}
          />
          <OrganizationFeatureField
            label={strings.form.fields.features.label}
            value={features}
            onChange={setFeatures}
            disabled={isSubmitting}
          />
        </div>
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end">
          <Button size="md" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? `${strings.actions.submit}${commonStrings.loadingEllipsis}`
              : strings.actions.submit}
          </Button>
        </div>
      </form>
    </ManagementFormLayout>
  )
}
